<template>
  <page-layout
      :title="pageTitle"
      :breadcrumb="pageBreadcrumb"
      :status="pageStatus"
      :code="pageCode"
      :info-cards="pageInfoCards"
  >
    <template #actions>
      <md-button class="md-primary md-round" @click="goToEdit(role.id)">
        <md-icon>edit</md-icon>
        Edit Role
      </md-button>
      <md-button class="md-info md-round" @click="goBack">
        <md-icon>arrow_back</md-icon>
        Back
      </md-button>
    </template>

    <template #content>
      <page-tabs>
        <!-- Permissions Tab -->
        <md-tab id="tab-permissions" md-label="Permissions" md-icon="lock">
          <div class="tab-content">
            <table-data
                ref="permissionsTable"
                title="Assigned Permissions"
                :columns="permissionColumns"
                :data="role.permissions"
                :total="role.permissions.length"
                :searchable="true"
                search-placeholder="Search permissions..."
                :has-actions="true"
            >
              <template #toolbar-right>
                <md-button class="md-primary md-round" @click="addPermission">
                  <md-icon>add</md-icon>
                  Assign Permission
                </md-button>
              </template>

              <template #cell-name="{ item }">
                <clickable-link
                    :id="item.id"
                    resource="Permission"
                    :text="item.display_name"
                    :stop-propagation="true"
                />
                <br/><small class="text-muted">{{item.name}}</small>
              </template>

              <template #cell-guard_name="{ item }">
                <span class="guard-chip">
                  <md-icon>vpn_key</md-icon>
                  {{ item.guard_name || 'web' }}
                </span>
              </template>

              <template #cell-created_at="{ item }">
                {{ formatDate(item.created_at) }}
              </template>

              <template #actions="{ item }">
                <md-button
                    class="md-icon-button md-danger"
                    @click="removePermission(item)"
                    title="Remove"
                >
                  <md-icon>delete</md-icon>
                </md-button>
              </template>

              <template #empty-actions>
                <md-button class="md-primary md-round" @click="addPermission">
                  <md-icon>add</md-icon>
                  Assign First Permission
                </md-button>
              </template>
            </table-data>
          </div>
        </md-tab>

        <!-- Users Tab -->
        <md-tab id="tab-users" md-label="Users" md-icon="supervisor_account">
          <div class="tab-content">
            <table-data
                ref="usersTable"
                title="Assigned Users"
                :columns="userColumns"
                :data="role.users"
                :total="role.users.length"
                :searchable="true"
                search-placeholder="Search users..."
                :has-actions="true"
            >
              <template #toolbar-right>
                <md-button class="md-primary md-round" @click="addUser">
                  <md-icon>person_add</md-icon>
                  Assign User
                </md-button>
              </template>

              <template #cell-name="{ item }">
                <clickable-link
                    :id="item.id"
                    resource="User"
                    :text="item.full_name"
                    :stop-propagation="true"
                />
                <br/><small class="text-muted">{{item.name}}</small>
              </template>

              <template #cell-email="{ item }">
                <a :href="'mailto:' + item.email" class="email-link">
                  {{ item.email }}
                </a>
              </template>

              <template #cell-created_at="{ item }">
                {{ formatDate(item.created_at) }}
              </template>

              <template #actions="{ item }">
                <md-button
                    class="md-icon-button md-danger"
                    @click="removeUser(item)"
                    title="Remove"
                >
                  <md-icon>delete</md-icon>
                </md-button>
              </template>

              <template #empty-actions>
                <md-button class="md-primary md-round" @click="addUser">
                  <md-icon>person_add</md-icon>
                  Assign First User
                </md-button>
              </template>
            </table-data>
          </div>
        </md-tab>
      </page-tabs>
      <modal v-if="addUserModel" @close="addUserModalHide">
        <template slot="header">
          <h4 class="modal-title">Add User to {{role.name}} role</h4>
          <md-button
              class="md-simple md-just-icon md-round modal-default-button"
              @click="addUserModalHide">
            <md-icon>clear</md-icon>
          </md-button>
        </template>

        <template slot="body">
<!--          <div class="form-field">-->
<!--            <label class="field-label">Target *</label>-->
<!--            <select-input-->
<!--                v-model="new_user"-->
<!--                track-by="id"-->
<!--                name="name"-->
<!--                placeholder="search user"-->
<!--                :options="users"-->
<!--                :allow-empty="true"-->
<!--                :loading="isSearchingUser"-->
<!--                :reduce-to-value="true"-->
<!--                @search="onUserSearch"-->
<!--                @open="onUserDropdownOpen"-->
<!--                @option:selected="onTargetOptionSelected"-->
<!--            >-->
<!--              <template #option="option">-->
<!--                <div class="option-row">-->
<!--                  <strong>{{ option.name }}</strong>-->
<!--                  <small v-if="option.emp_code">{{ option.emp_code }}</small>-->
<!--                </div>-->
<!--              </template>-->

<!--              <template #no-options="{ search, searching, loading }">-->
<!--                <div class="no-options">-->
<!--                  <span v-if="loading">Searching...</span>-->
<!--                  <span v-else-if="search && search.length < 2">Type at least 2 characters</span>-->
<!--                  <span v-else-if="searching">No labourers match "{{ search }}"</span>-->
<!--                  <span v-else>Type to search labourers...</span>-->
<!--                </div>-->
<!--              </template>-->
<!--            </select-input>-->
<!--            <span class="md-error" v-if="errors.target_id">{{ errors.target_id }}</span>-->
<!--          </div>-->
          <multiselect
              v-model="new_user"
              deselect-label="Can't remove this value"
              track-by="id"
              :multiple="multiple"
              label="full_name"
              placeholder="Select user to Add"
              :options="users"
              :searchable="true"
              :allow-empty="false">
          </multiselect>
        </template>

        <template slot="footer">
          <md-button class="md-simple" @click="addUserSubmit">Add</md-button>
          <md-button
              class="md-danger md-simple"
              @click="addUserModalHide">Close
          </md-button>
        </template>
      </modal>

      <modal v-if="addPermissionModel" @close="hidePermissionModal">
        <template slot="header">
          <h4 class="modal-title">Add Permission to {{role.name}} Role</h4>
          <md-button
              class="md-simple md-just-icon md-round modal-default-button"
              @click="hidePermissionModal">
            <md-icon>clear</md-icon>
          </md-button>
        </template>

        <template slot="body">
          <multiselect
              v-model="new_permission"
              deselect-label="Can't remove this value"
              track-by="name"
              :multiple="multiple"
              label="name"
              placeholder="Select Permission to Add"
              :options="permissions"
              :searchable="true"
              :allow-empty="false">
          </multiselect>
        </template>

        <template slot="footer">
          <md-button class="md-simple" @click="addPermissionSubmit">Add Selected Permission</md-button>
          <md-button
              class="md-danger md-simple"
              @click="hidePermissionModal">Close
          </md-button>
        </template>
      </modal>
    </template>
  </page-layout>
</template>

<script>
import PageLayout from '@/components/Layouts/PageLayout.vue';
import PageTabs from '@/components/Layouts/PageTabs.vue';
import TableData from '@/components/DataTable/TableData.vue';
import ClickableLink from '@/components/ClickableLink.vue';
import PageMixin from '@/mixins/page-mixin';
import formMixin from '@/mixins/form-mixin';
import Swal from 'sweetalert2';
import moment from 'moment';
import { Modal } from '@/components';
// import SelectInput from "@/components/Inputs/SelectInput.vue";
import Multiselect from "vue-multiselect";

export default {
  name: 'ViewRolePage',
  components: {
    Multiselect,
    PageLayout,
    Modal,
    PageTabs,
    TableData,
    ClickableLink,
  },
  mixins: [PageMixin, formMixin],
  data() {
    return {
      role: {
        type: 'roles',
        name: null,
        id: null,
        group: null,
        description: null,
        guard_name: null,
        is_system: false,
        permissions: [],
        users: [],
        created_at: null,
        updated_at: null
      },
      multiple: true,
      addUserModel: false,
      addPermissionModel: false,
      new_user: [],
      new_permission: [],
      users: [],
      permissions: [],
      permissionColumns: [
        { field: 'name', label: 'Permission' },
        { field: 'guard_name', label: 'Guard' },
        { field: 'created_at', label: 'Created' }
      ],
      userColumns: [
        { field: 'name', label: 'Name' },
        { field: 'email', label: 'Email' },
        { field: 'created_at', label: 'Created' }
      ]
    };
  },
  computed: {
    pageTitle: function () {
      return this.role.name || 'Role Details';
    },
    pageBreadcrumb: function () {
      return [
        { label: 'Settings', path: '/settings' },
        { label: 'Roles', path: '/roles' },
        { label: this.role.name || 'Details' }
      ];
    },
    pageStatus: function () {
      return {
        label: this.role.guard_name || 'web',
        type: 'info'
      };
    },
    pageCode: function () {
      return this.role.is_system ? '🔒 System Role' : null;
    },
    pageInfoCards: function () {
      return [
        {
          icon: 'vpn_key',
          label: 'Permissions',
          value: this.role.permissions ? this.role.permissions.length : 0,
          color: 'primary'
        },
        {
          icon: 'people',
          label: 'Users',
          value: this.role.users ? this.role.users.length : 0,
          color: 'success'
        },
        {
          icon: 'category',
          label: 'Group',
          value: this.role.group || 'Ungrouped',
          color: 'info'
        },
        {
          icon: 'schedule',
          label: 'Last Updated',
          value: this.role.updated_at ? moment(this.role.updated_at).fromNow() : 'N/A',
          color: 'warning'
        }
      ];
    }
  },
  created() {
    this.get();
  },
  methods: {
    // ============ DATA FETCHING ============
    async get() {
      try {
        var id = this.$route.params.id;
        await this.$store.dispatch('roles/get', id);
        this.role = this.$store.getters['roles/role'];

        // Ensure arrays exist
        this.role.permissions = this.role.permissions || [];
        this.role.users = this.role.users || [];

      } catch (error) {
        console.error('Error fetching role:', error);
        this.notifyException(error);
      }
    },

    async update() {
      try {
        await this.$store.dispatch('roles/update', this.role);
        this.$store.dispatch('alerts/success', 'Role updated successfully.');
      } catch (error) {
        if (error.response && error.response.data && error.response.data.errors) {
          var errors = error.response.data.errors;
          if (Array.isArray(errors) && errors[0]) {
            this.$store.dispatch('alerts/error', errors[0].title || errors[0].detail);
          }
        } else {
          this.$store.dispatch('alerts/error', 'Failed to update role');
        }
      }
    },

    // ============ PERMISSION MANAGEMENT ============
    async addPermission() {
      try {
        if(this.permissions.length < 1){
          await this.$store.dispatch('permissions/list', { page: { size: 1000 } });
          this.permissions = this.$store.getters['permissions/list'] || [];
        }

        this.new_permission = [];
        this.addPermissionModel = true;
      } catch (error) {
        this.$store.dispatch('alerts/error', 'Failed to load permissions');
      }
    },

    hidePermissionModal() {
      this.addPermissionModel = false;
    },

    async addPermissionSubmit() {
      try {
        if (!this.new_permission || this.new_permission.length === 0) {
          this.hidePermissionModal();
          return;
        }

        var existingIds = this.role.permissions.map(function (p) { return p.id; });
        var updateNeeded = false;
        var self = this;

        this.new_permission.forEach(function (permission) {
          if (existingIds.indexOf(permission.id) === -1) {
            self.role.permissions.push(permission);
            updateNeeded = true;
          } else {
            self.$store.dispatch(
                'alerts/error',
                'Permission "' + permission.name + '" is already assigned'
            );
          }
        });

        if (updateNeeded) {
          await this.update();
          await this.get();
        }

        this.hidePermissionModal();
        this.new_permission = [];
      } catch (error) {
        this.$store.dispatch('alerts/error', 'Failed to add permission');
      }
    },

    removePermission(item) {
      var self = this;
      Swal.fire({
        title: 'Remove Permission?',
        text: 'Remove "' + item.name + '" from role "' + this.role.name + '"?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#d33'
      }).then(async function (result) {
        if (!result.value) return;

        var index = self.role.permissions.findIndex(function (p) {
          return p.id === item.id;
        });

        if (index >= 0) {
          self.role.permissions.splice(index, 1);
          await self.update();
          await self.get();
          self.$store.dispatch('alerts/success', 'Permission removed');
        }
      });
    },

    // ============ USER MANAGEMENT ============
    async addUser() {
      try {
        await this.$store.dispatch('users/list', { page: { size: 1000 } });
        this.users = this.$store.getters['users/list'] || [];
        this.new_user = [];
        this.addUserModel = true;
      } catch (error) {
        this.$store.dispatch('alerts/error', 'Failed to load users');
      }
    },

    addUserModalHide() {
      this.addUserModel = false;
    },

    async addUserSubmit() {
      try {
        if (!this.new_user || this.new_user.length === 0) {
          this.addUserModalHide();
          return;
        }

        var existingIds = this.role.users.map(function (u) { return u.id; });
        var updateNeeded = false;
        var self = this;

        this.new_user.forEach(function (user) {
          if (existingIds.indexOf(user.id) === -1) {
            self.role.users.push(user);
            updateNeeded = true;
          } else {
            self.$store.dispatch(
                'alerts/error',
                'User "' + user.name + '" is already assigned'
            );
          }
        });

        if (updateNeeded) {
          await this.update();
          await this.get();
        }

        this.addUserModalHide();
        this.new_user = [];
      } catch (error) {
        this.$store.dispatch('alerts/error', 'Failed to add user');
      }
    },

    removeUser(item) {
      var self = this;
      Swal.fire({
        title: 'Remove User?',
        text: 'Remove "' + item.name + '" from role "' + this.role.name + '"?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#d33'
      }).then(async function (result) {
        if (!result.value) return;

        var index = self.role.users.findIndex(function (u) {
          return u.id === item.id;
        });

        if (index >= 0) {
          self.role.users.splice(index, 1);
          await self.update();
          await self.get();
          self.$store.dispatch('alerts/success', 'User removed');
        }
      });
    },

    // ============ NAVIGATION ============
    goToEdit(id) {
      this.$router.push({ name: 'Edit Role', params: { id: id } });
    },

    goBack() {
      this.$router.push({ name: 'List Roles' });
    },

    // ============ HELPERS ============
    formatDate(date) {
      if (!date) return 'N/A';
      return moment(date).format('DD MMM YYYY');
    }
  }
};
</script>

<style lang="scss" scoped>
.tab-content {
  padding: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.guard-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  background: #e3f2fd;
  color: #1565c0;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;

  .md-icon {
    font-size: 14px !important;
    width: 14px !important;
    height: 14px !important;
  }
}

.email-link {
  color: #667eea;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>